import * as angular from "angular";
import { SurveyService } from "../services/survey.service";

export class SurveyDataProvider {
    public surveys: any = null;
    public allSurveysPromise: any;
    public surveyLists: any = null;
    public surveyListsPromise: any;

    private $q: any;
    private service: SurveyService;

    constructor($q: any, SurveyService: SurveyService) {
        this.$q = $q;
        this.service = SurveyService;
    }

    /**
     * returns all of the data
     */
    getAllSurveys(): Promise<any> {
        if (this.surveys !== null) {
            return this.$q.resolve(this.surveys);
        } else if (this.allSurveysPromise) {
            //Has been called already and has not resolved yet.
            return this.allSurveysPromise;
        } else {
            const deferred = this.$q.defer();
            this.service.all().then(
                (response: object) => {
                    const json = response["data"];
                    this.surveys = [];
                    if (json.data) {
                        for (let i = 0; i < json.data.length; i++) {
                            this.surveys.push(json.data[i]);
                        }
                    }
                    deferred.resolve(this.surveys);
                },
                (error: object) => {
                    this.surveys = [];
                    deferred.reject(error);
                }
            );
            this.allSurveysPromise = deferred.promise;
            return this.allSurveysPromise;
        }
    }

    /**
     * returns all of the data
     */
    getSurveyLists(): Promise<any> {
        if (this.surveyLists !== null) {
            return this.$q.resolve(this.surveyLists);
        } else if (this.surveyListsPromise) {
            //Has been called already and has not resolved yet.
            return this.allSurveysPromise;
        } else {
            const deferred = this.$q.defer();
            this.surveyLists = { live: [], notlive: [], liveDisabled: [] };
            this.getAllSurveys().then((surveys: any) => {
                angular.forEach(surveys, (survey: any) => {
                    if (
                        survey.osid &&
                        survey.disabled == "1" &&
                        survey.sstatus == "Live"
                    ) {
                        this.surveyLists.liveDisabled.push(survey);
                    } else if (survey.sstatus == "Live") {
                        this.surveyLists.live.push(survey);
                    } else if (survey.sstatus == "Not Live") {
                        this.surveyLists.notlive.push(survey);
                    }
                });

                this.surveyLists.live.sort(
                    (a: any, b: any): number => a.order - b.order
                );
                this.surveyLists.liveDisabled.sort(
                    (a: any, b: any): number => a.order - b.order
                );
                this.surveyLists.notlive.sort(
                    (a: any, b: any): number => a.order - b.order
                );
                deferred.resolve(this.surveyLists);
            });

            this.allSurveysPromise = deferred.promise;
            return this.allSurveysPromise;
        }
    }

    create(survey: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service.create(survey).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                this.replaceSurvey(json.data);
                this.clearSurveyLists();
                deferred.resolve(json.data);
            } else {
                deferred.reject();
            }
        });
        return deferred.promise;
    }

    update(survey: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service.update(survey).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                angular.extend(survey, json.data);
                this.replaceSurvey(survey);

                this.clearSurveyLists();
                deferred.resolve(json.data);
            } else {
                deferred.reject();
            }
        });
        return deferred.promise;
    }

    replaceSurvey(survey: any): void {
        let existingSurvey = this.surveyWithID(survey.sid);
        if (existingSurvey) {
            angular.extend(existingSurvey, survey);
        } else {
            if (this.surveys) this.surveys.push(survey);
        }
    }

    surveyWithID(sid: string | number): any {
        if (this.surveys && this.surveys.length) {
            for (let i = 0; i < this.surveys.length; i++) {
                if (this.surveys[i].sid == sid) {
                    return this.surveys[i];
                }
            }
        }
        return null;
    }

    updateOrder(data: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service.updateOrder(data).then(() => {
            deferred.resolve(data);
            this.clearAll();
        });
        return deferred.promise;
    }

    updateQuestionOrder(surveyId: number, data: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service.updateQuestionOrder(surveyId, data).then(() => {
            deferred.resolve(data);
            this.clearAll();
        });
        return deferred.promise;
    }

    clearAll(): void {
        this.surveys = null;
        this.surveyLists = null;
        this.allSurveysPromise = null;
    }

    clearSurveyLists(): void {
        this.surveyLists = null;
    }

    surveyInfo(sId: number | string): Promise<any> {
        const deferred = this.$q.defer();
        this.service.info(sId).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                return deferred.resolve(json.data);
            }
            deferred.reject();
        });
        return deferred.promise;
    }

    spliceSurvey(survey: any, surveys: any[]): void {
        if (surveys !== null) {
            const surveyIndex = surveys.map((s) => s.sid).indexOf(survey.sid);
            if (surveyIndex > -1) {
                surveys.splice(surveyIndex, 1);
            }
        }
    }

    processSocketUpdate(socketData) {
        if (this.surveys) {
            this.replaceSurvey(socketData);
        }
    }

    processSocketCreate(socketData) {
        if (this.surveys) {
            this.replaceSurvey(socketData);
        }
    }

    processSocketDelete(socketData: any): void {
        if (this.surveys) {
            let existingSurvey = this.surveyWithID(socketData.tid);
            if (existingSurvey) {
                this.spliceSurvey(existingSurvey, this.surveys);
            }
        }
    }
}

angular
    .module("vcDataProvider")
    .service("SurveyDataProvider", SurveyDataProvider);
